import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { useMsal } from '@azure/msal-react';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import { queryClient } from '@/lib/queryClient';
import { msalInstance } from '@/features/auth/authConfig';
import { ErrorBoundary } from './ErrorBoundary';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { AccessDeniedPage } from '@/features/auth/AccessDeniedPage';
import { AppLayout } from './AppLayout';
import { MableHomePage } from '@/features/home/MableHomePage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { ImportantDatesPage } from '@/features/settings/important-dates/ImportantDatesPage';
import { UserManagementPage } from '@/features/settings/user-management/UserManagementPage';
import { DraftCalendarPage } from '@/features/draft-calendar/DraftCalendarPage';
import { DraftCalendarView } from '@/features/draft-calendar/components/DraftCalendarView';
import { TableauMeetingDetails } from '@/features/draft-calendar/components/TableauMeetingDetails';
import { useFiscalYears } from '@/services/draftCalendar';
import { useUserByEmail } from '@/services/settings';
import { getAllowedNavItems, isApproverRole, shouldSkipDashboardForUser } from './navigation';
import { MessageMonitorPage } from '@/features/message-monitor/MessageMonitorPage';
import { GRCDashboardPage } from '@/features/grc/GRCDashboardPage';

export function AppProviders() {
  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={import.meta.env.BASE_URL}>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route path="home" element={<MableHomePage />} />
                <Route path="dashboard" element={<DashboardLandingRoute />} />
                <Route path="grcdashboard" element={<GRCDashboardPage />} />
                <Route path="draftcalendar" element={<DraftCalendarPage />} />
                <Route path="calendarview" element={<DraftCalendarView />} />
                <Route path="tableau/meeting-details" element={<TableauMeetingDetailsRoute />} />
                <Route path="settings" element={<SettingsPage />}>
                  <Route index element={<Navigate to="important-dates" replace />} />
                  <Route path="important-dates" element={<ImportantDatesPage />} />
                  <Route path="user-management" element={<UserManagementPage />} />
                </Route>
                <Route path="message-monitor" element={<MessageMonitorPage />} />
              </Route>
              <Route path="access-denied" element={<AccessDeniedPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" />
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
}

function DashboardLandingRoute() {
  const { accounts } = useMsal();
  const email = accounts[0]?.username ?? '';
  const { data: user, isPending: loadingUser } = useUserByEmail(email, { enabled: Boolean(email) });
  const { data: fiscalYears = [], isPending: loadingFY } = useFiscalYears();
  const userHasApproverRole = isApproverRole(user);

  if ((Boolean(email) && loadingUser) || (userHasApproverRole && loadingFY)) {
    return <LandingRouteSpinner />;
  }

  const shouldSkipDashboard = shouldSkipDashboardForUser(user, fiscalYears, loadingFY);
  const nextAllowedPath = getAllowedNavItems(user, shouldSkipDashboard)[0]?.href;

  if (shouldSkipDashboard && nextAllowedPath) {
    return <Navigate to={nextAllowedPath} replace />;
  }

  return <DashboardPage />;
}

function LandingRouteSpinner() {
  return (
    <div className="flex min-h-[calc(100vh-96px)] items-center justify-center gap-3 text-slate-600" role="status" aria-live="polite">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span className="text-sm">Loading...</span>
    </div>
  );
}

function TableauMeetingDetailsRoute() {
  const { data: fiscalYears = [] } = useFiscalYears();
  const tableauFyYear = fiscalYears[1]?.code ?? fiscalYears[0]?.code ?? '';

  return <TableauMeetingDetails FyYear={tableauFyYear} />;
}
